// Every acronym the player meets, expanded on hover.
//
// Michael's standing rule is that an acronym is written out in parentheses on first use. That works in
// prose; it does not work in a top bar chip or a report line, where there is room for three letters and
// nothing else. So the rule gets a second half: wherever the short form has to stand alone, it is an
// `<Acronym>` — a dotted underline, the full term in the tooltip, and the same wording everywhere.
//
//   <Acronym k="NAI" />            -> NAI, hover: "NAI — named area of interest: ..."
//   <Acronym k="c2">c2 realistic</Acronym>  -> any label, the C2 gloss behind it
//
// The map is the single source of truth for what these letters mean, so the OPORD, the top bar, the
// deck hint and the advisor headers cannot drift apart.

import type { ReactNode } from 'react';

/**
 * term: what the letters stand for. note: the sentence that makes it useful to a non-planner.
 * short: how the abbreviation is written when it is not capitals ("vic", not "VIC").
 */
interface Term { term: string; note?: string; short?: string }

const TERMS: Record<string, Term> = {
  ao: { term: 'area of operations', note: 'The piece of ground this fight happens on.' },
  aa: { term: 'assembly area', note: 'Where the task force starts, before it crosses the line of departure.' },
  c2: { term: 'command and control', note: 'How long an order takes to reach a unit after your headquarters transmits it.' },
  casevac: { term: 'casualty evacuation', note: 'Pulling casualties back so a platoon can keep fighting.' },
  coa: { term: 'course of action', note: 'The plan you draw on the ground: axes, phase lines, objectives, tasks.' },
  cop: { term: 'common operating picture', note: 'What you can see: your own units as reported, and enemy contacts with their age and uncertainty. Not ground truth.' },
  ew: { term: 'electronic warfare', note: 'Enemy jamming: it stretches order delays and shortens how long a drone survives overhead.' },
  frago: { term: 'fragmentary order', note: 'A short change to the order already in force, written in plain language.' },
  hq: { term: 'headquarters', note: 'The unit that issues your orders; every card spends its time on the command net.' },
  isr: { term: 'intelligence, surveillance and reconnaissance', note: 'Everything you use to find the enemy before he finds you.' },
  nai: { term: 'named area of interest', note: 'A place you expect to learn something: put eyes on it and the question gets answered.' },
  obj: { term: 'objective', note: 'The ground the mission is to seize.' },
  opord: { term: 'operation order', note: 'The order you are given: situation, mission, intent, task organization.' },
  pl: { term: 'phase line', note: 'A line across the ground used to control and report the advance.' },
  sitrep: { term: 'situation report', note: 'The staff’s periodic summary of where the force stands.' },
  spotrep: { term: 'spot report', note: 'What one observer saw, when, and how sure of it they were.' },
  tf: { term: 'task force', note: 'Your command: a battalion-sized force of companies and supporting platoons.' },
  tod: { term: 'time of day', note: 'How far your sensors see, and how early you find the enemy.' },
  // Not an acronym but an abbreviation the feed uses in almost every report, and the one piece of
  // staff shorthand a non-planner is most likely to read straight past.
  vic: { short: 'vic', term: 'vicinity of', note: 'Near, not exactly at: the grid square a report puts something in.' },
  uas: { term: 'uncrewed aircraft system', note: 'A drone. It has to be launched, it is hunted, and it does not last forever.' },
  fso: { term: 'fire support coordinator', note: 'The officer who decides what the guns shoot at, and when.' },
  's-2': { term: 'the intelligence officer', note: 'Assesses the enemy. Everything the S-2 says is an estimate, and is labelled as one.' },
  's-3': { term: 'the operations officer', note: 'Runs the fight: phases, control measures and the orders that carry them.' },
  's-4': { term: 'the logistics officer', note: 'Fuel, ammunition and casualty evacuation — what decides how long you can keep attacking.' },
};

export type AcronymKey = keyof typeof TERMS | string;

/** The tooltip text for one key, for places that need the string rather than the element. */
export function acronymTitle(k: AcronymKey): string {
  const key = String(k).toLowerCase();
  const t = TERMS[key];
  if (!t) return String(k).toUpperCase();
  const head = `${t.short ?? String(k).toUpperCase()} — ${t.term}`;
  return t.note ? `${head}. ${t.note}` : head;
}

/** True when the key is one the map knows; lets callers fall back to plain text. */
export function knownAcronym(k: AcronymKey): boolean {
  return TERMS[String(k).toLowerCase()] !== undefined;
}

/**
 * One acronym in running copy or on a chip. Renders the short form (or whatever children are given)
 * with a dotted underline and the expansion in the native tooltip — no popover, no layout shift, and
 * it works inside a `<button>`, which is where half of these live.
 */
export function Acronym({ k, children, className }: { k: AcronymKey; children?: ReactNode; className?: string }) {
  const title = acronymTitle(k);
  return (
    <abbr
      data-acronym={String(k).toLowerCase()}
      title={title}
      className={`wt-acronym ${className ?? ''}`}
    >
      {children ?? String(k).toUpperCase()}
    </abbr>
  );
}

/**
 * Wraps every known acronym in a run of generated prose.
 *
 * The OPORD (operation order), the mission line and the commander's key tasks are built as strings by
 * `src/ui/opord.ts` from the fitted scenario, so their acronyms cannot be marked up by hand — the
 * objective is "OBJ CARBON" on one area of operations and "OBJ FLINT" on another. One pass over the
 * text gives the whole order the same hover behaviour as the chips.
 *
 * Used on the report feed too. That rail holds a hundred and sixty cards and the snapshot changes ten
 * times a second, so the split would be ruinous per frame - but a report is templated once, when its
 * event arrives, and `EventCard` is memoized on it (src/ui/cards/EventCard.tsx), so the split runs once
 * per card for the life of the run.
 */
const TOKEN = /\b(OPORD|SITREP|SPOTREP|FRAGO|CASEVAC|COA|NAI|OBJ|COP|UAS|ISR|AA|EW|TOD|HQ|TF|PL|C2|S-[234]|vic)\b/g;

export function AcronymText({ children }: { children: string }) {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(children)) !== null) {
    if (m.index > last) out.push(children.slice(last, m.index));
    // `vic` is lower case in the feed's own prose and stays that way; everything else is capitals.
    out.push(<Acronym key={`${m.index}-${m[0]}`} k={m[0]}>{m[0]}</Acronym>);
    last = m.index + m[0].length;
  }
  if (out.length === 0) return <>{children}</>;
  if (last < children.length) out.push(children.slice(last));
  return <>{out}</>;
}
